import axios from 'axios'
import * as Sentry from '@sentry/vue'
import {
  EDITOR_NETWORK_TIMEOUT_MS,
  EDITOR_PATCH_TIMEOUT_MS,
} from '../constants/editorNetwork'
import wrappers from '../utils/wrappers'
import {
  isChapterContentDeletionRequiresForceError,
  isChapterVersionConflictError,
} from '../utils/chapterErrors'
import { isConnectivityError } from '../utils/isConnectivityError'

const url = `${import.meta.env.VITE_API_URL}/v1/chapters`

function sanitizeChapterPatchParams(params: Record<string, unknown> = {}) {
  if (!('content' in params) || params.content === undefined) {
    return params
  }

  const { content: _content, ...rest } = params

  Sentry.withScope((scope) => {
    scope.setLevel('warning')
    scope.setTag('feature', 'chapter-content-desync')
    scope.setExtra('paramKeys', Object.keys(params))
    Sentry.captureMessage('Attempted chapters.patch with content field')
  })

  return rest
}

export default {
  list(params = {}) {
    return axios
      .get(url, { params })
      .then((response: any) => wrappers.handleEscribonautaResponse(response))
  },

  create(params = {}) {
    return axios
      .post(url, params)
      .then((response: any) => wrappers.handleEscribonautaResponse(response))
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudo crear el capítulo'),
      )
  },

  get(chapterId: string) {
    return axios
      .get(`${url}/${chapterId}`, { timeout: EDITOR_NETWORK_TIMEOUT_MS })
      .then((response: any) => wrappers.handleEscribonautaResponse(response))
  },

  checkContentSync(chapterId: string, expectedContentHash?: string | null) {
    const params =
      expectedContentHash === undefined || expectedContentHash === null
        ? {}
        : { expectedContentHash }
    return axios
      .get(`${url}/${chapterId}/content-sync`, {
        params,
        timeout: EDITOR_NETWORK_TIMEOUT_MS,
      })
      .then((response: any) => wrappers.handleEscribonautaResponse(response))
  },

  reorder(chapterId: string, params = {}) {
    return axios
      .patch(`${url}/${chapterId}/reorder`, params)
      .then((response: any) => wrappers.handleEscribonautaResponse(response))
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudo reordenar los capítulos'),
      )
  },

  patchMeta(
    chapterId: string,
    params: Record<string, unknown> = {},
    requestOptions?: { silentConnectivityError?: boolean; silentError?: boolean },
  ) {
    return axios
      .patch(`${url}/${chapterId}/meta`, params, { timeout: EDITOR_NETWORK_TIMEOUT_MS })
      .then((response: any) => wrappers.handleEscribonautaResponse(response))
      .catch((error: any) => {
        const suppressConnectivityToast = Boolean(
          requestOptions?.silentConnectivityError && isConnectivityError(error),
        )
        const shouldShowToast = !requestOptions?.silentError && !suppressConnectivityToast
        if (shouldShowToast) {
          return wrappers.handleEscribonautaError(error, 'No se pudo guardar la información del capítulo')
        }
        return Promise.reject(error)
      })
  },

  patch(
    chapterId: string,
    params = {},
    requestOptions?: { silentConnectivityError?: boolean; silentError?: boolean },
  ) {
    const sanitizedParams = sanitizeChapterPatchParams(params as Record<string, unknown>)

    return axios
      .patch(`${url}/${chapterId}`, sanitizedParams, { timeout: EDITOR_PATCH_TIMEOUT_MS })
      .then((response: any) => wrappers.handleEscribonautaResponse(response))
      .catch((error: any) => {
        const suppressConnectivityToast = Boolean(
          requestOptions?.silentConnectivityError && isConnectivityError(error),
        )
        // 409 conflict and mass-deletion force prompts are handled by the store UI.
        const shouldShowToast =
          !requestOptions?.silentError
          && !suppressConnectivityToast
          && !isChapterContentDeletionRequiresForceError(error)
          && !isChapterVersionConflictError(error)
        if (shouldShowToast) {
          return wrappers.handleEscribonautaError(error, 'No se pudo editar el capítulo')
        }
        return Promise.reject(error)
      })
  },

  getAssetDownloadUrl(chapterId: string, assetKey: string) {
    return axios
      .get(`${url}/${chapterId}/assets/${assetKey}/download-url`)
      .then((response: any) => wrappers.handleEscribonautaResponse(response))
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudo obtener la imagen'),
      )
  },

  restore(chapterId: string, params = {}) {
    return axios
      .post(`${url}/${chapterId}/restore`, params)
      .then((response: any) => wrappers.handleEscribonautaResponse(response))
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudo restaurar el capítulo'),
      )
  },

  delete(chapterId: string) {
    return axios
      .delete(`${url}/${chapterId}`)
      .then((response: any) => wrappers.handleEscribonautaResponse(response))
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudo eliminar el capítulo'),
      )
  },
}
